import type {
    DwtSubmissionMode,
    IDwtCorrectionAttempt,
    IDwtReceipt,
    IWasteMovementDwtCorrectResponse,
    IWasteMovementDwtRefreshResponse,
    IWasteMovementDwtSubmission,
    IWasteMovementDwtSubmitResponse,
} from "@/customTypings/wasteMovement/WasteMovement";
import { SITE_TIME_ZONE } from "./wasteMovementReceiptTime";

/**
 * S2B/S2C/S2D: operator-facing wording for DEFRA DWT submission, status refresh and correction of a filing. Everything here
 * is display only - the server decides what is submitted, checked or corrected, in which mode, and whether it is permitted.
 */

export type DwtVariant = "success" | "info" | "warning" | "danger" | "secondary";

/** A message about the last submit request that the persisted state alone does not convey. */
export type DwtSubmitFeedback = { variant: DwtVariant; message: string };

/** The mode as the operator must see it; LIVE is a real regulatory filing. */
export function dwtModeBadge(mode: string | null | undefined): { label: string; variant: DwtVariant; description: string } {
    switch (mode) {
        case "live":
            return { label: "LIVE", variant: "danger", description: "Real filing with DEFRA" };
        case "test":
            return { label: "TEST", variant: "warning", description: "DEFRA test service - not a real filing" };
        case "dry":
            return { label: "DRY", variant: "secondary", description: "Checks only - nothing is sent to DEFRA" };
        default:
            return { label: "NOT CONFIGURED", variant: "secondary", description: "Submission is not configured on this server" };
    }
}

/** The submit button's words: never claims a DEFRA submission in dry mode. */
export function dwtSubmitButtonLabel(mode: DwtSubmissionMode | string | null | undefined): string {
    switch (mode) {
        case "live":
            return "Submit to DEFRA";
        case "test":
            return "Submit to DEFRA Test";
        case "dry":
            return "Validate DWT submission (Dry)";
        default:
            return "Submit to DEFRA";
    }
}

/** The confirmation button's words; LIVE says plainly that it files with DEFRA. */
export function dwtConfirmButtonLabel(mode: DwtSubmissionMode | string | null | undefined): string {
    switch (mode) {
        case "live":
            return "Yes, file with DEFRA (LIVE)";
        case "test":
            return "Yes, submit to DEFRA Test";
        default:
            return "Yes, run the dry validation";
    }
}

export function dwtModeName(mode: string | null | undefined): string {
    switch (mode) {
        case "live":
            return "DEFRA Live";
        case "test":
            return "DEFRA Test";
        case "dry":
            return "Dry run";
        default:
            return "an unknown mode";
    }
}

const ukInstantFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: SITE_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short",
});

/** A UTC instant from the API shown in UK time, e.g. `24/09/2026, 11:00:01 BST`, whatever the browser's zone. */
export function formatUkInstant(utcIso: string | null | undefined): string {
    if (!utcIso) return "";
    const iso = /[zZ]|[+-]\d{2}:\d{2}$/.test(utcIso) ? utcIso : `${utcIso}Z`;
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? utcIso : ukInstantFormatter.format(new Date(ms));
}

/**
 * The canonical receipt time exactly as the receipt states it (UK wall clock with its offset), without converting through
 * the browser's zone: `2026-09-22T09:15:00+01:00` → `22/09/2026 09:15:00 BST`.
 */
export function formatReceiptTime(isoWithOffset: string | null | undefined): string {
    if (!isoWithOffset) return "Not recorded";
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:\d{2})$/.exec(isoWithOffset);
    if (!m) return isoWithOffset;
    const zone = m[7] === "+01:00" ? "BST" : m[7] === "+00:00" || m[7] === "Z" ? "GMT" : `UTC${m[7]}`;
    return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}:${m[6] ?? "00"} ${zone}`;
}

export function formatTonnes(weight: number | null | undefined, estimate: boolean | null | undefined): string {
    if (weight == null) return "Not recorded";
    const text = `${weight.toLocaleString("en-GB", { maximumFractionDigits: 6 })} t`;
    return estimate ? `${text} (estimate)` : text;
}

/** `17 05 04` from DEFRA's six digits, with `*` when the code is classified hazardous. */
export function formatEwc(code: string | null | undefined, hazardous: boolean | null | undefined): string {
    if (!code) return "Not recorded";
    const spaced = /^\d{6}$/.test(code) ? `${code.slice(0, 2)} ${code.slice(2, 4)} ${code.slice(4, 6)}` : code;
    return hazardous ? `${spaced}*` : spaced;
}

/** The DEFRA D/R code with the Froom EA code it was derived from, both exactly as the canonical receipt holds them. */
export function formatDisposalOrRecovery(receipt: IDwtReceipt): string {
    const item = receipt.wasteItem;
    const defra = item.disposalOrRecovery?.code;
    const ea = item.eaDisposalRecoveryCode;
    if (defra && ea) return `${defra} (derived from Froom EA code ${ea})`;
    if (defra) return defra;
    if (ea) return `None - Froom EA code ${ea} has no DEFRA equivalent`;
    return "None recorded";
}

export function formatHazardous(receipt: IDwtReceipt): string {
    const item = receipt.wasteItem;
    if (item.containsHazardous == null) return "Not recorded";
    if (!item.containsHazardous) return "No";
    const codes = item.hazardous?.hazardousPropertyCodes ?? [];
    return codes.length > 0 ? `Yes (${codes.map((c) => c.replace("_", " ")).join(", ")})` : "Yes";
}

export function formatPops(receipt: IDwtReceipt): string {
    const item = receipt.wasteItem;
    if (item.containsPops == null) return "Not recorded";
    if (!item.containsPops) return "No";
    const codes = (item.pops?.components ?? []).map((c) => c.code);
    return codes.length > 0 ? `Yes (${codes.join(", ")})` : "Yes";
}

export function formatCarrier(receipt: IDwtReceipt): string {
    const carrier = receipt.carrier;
    if (!carrier?.organisationName) return "Not recorded";
    const registration = carrier.registrationNumber ?? (carrier.reasonForNoRegistrationNumber ? `no registration: ${carrier.reasonForNoRegistrationNumber}` : null);
    const parts = [registration, carrier.vehicleRegistration].filter((p): p is string => !!p);
    return parts.length > 0 ? `${carrier.organisationName} (${parts.join(", ")})` : carrier.organisationName;
}

export function formatBroker(receipt: IDwtReceipt): string | null {
    const broker = receipt.brokerDealer;
    if (!broker?.organisationName) return null;
    return broker.registrationNumber ? `${broker.organisationName} (${broker.registrationNumber})` : broker.organisationName;
}

/** Who must act on an AxiWaste `failed` reason (its FailureReason codes). */
export function dwtFailureReasonText(reason: string | null | undefined): string {
    switch (reason) {
        case "charge":
            return "The DEFRA service charge is unpaid. The filing organisation must pay it.";
        case "credentials":
            return "DEFRA refused the credentials AxiWaste holds. AxiWaste must fix them.";
        case "retries_exhausted":
            return "DEFRA could not be reached. AxiWaste keeps retrying once DEFRA is back.";
        case "not_held":
            return "DEFRA has no record of the tracking ID AxiWaste holds. AxiWaste must resolve it.";
        case "blocked":
            return "The AxiWaste account is suspended or the mode is no longer permitted. Contact AxiWaste.";
        case "defra_refused":
            return "DEFRA refused it for another reason. AxiWaste is investigating.";
        default:
            return "AxiWaste could not file it. Contact AxiWaste.";
    }
}

/** What an AxiWaste refusal code means for the operator (nothing was filed for any of them). */
export function dwtRefusalText(code: string | null | undefined): string {
    switch (code) {
        case "API_KEY_REQUIRED":
        case "API_KEY_INVALID":
            return "AxiWaste did not accept this server's API key. An administrator must check the AxiWaste configuration.";
        case "CLIENT_SUSPENDED":
            return "The AxiWaste account is suspended. Contact AxiWaste.";
        case "LIVE_NOT_ALLOWED":
            return "LIVE filing is not enabled for this AxiWaste account. Contact AxiWaste.";
        case "DUPLICATE_TICKET":
            return "This weighbridge ticket is already filed or in flight under another movement. Check for a duplicate movement before trying again.";
        case "MOVEMENT_TOO_LARGE":
            return "The movement is too large for AxiWaste. Shorten the longest text (for example the description) and try again.";
        case "UNKNOWN_FIELD":
        case "BAD_MODE":
        case "MOVEMENT_REQUIRED":
        case "BAD_REFERENCE":
            return "AxiWaste could not read the request. Contact support.";
        default:
            return "AxiWaste refused the request before processing it.";
    }
}

export type DwtStatusView = { title: string; variant: DwtVariant; explanation: string };

/** The headline for a submission state; the provider's own words are shown alongside, never branched on. */
export function dwtStatusView(submission: IWasteMovementDwtSubmission): DwtStatusView {
    const mode = dwtModeName(submission.statusMode);
    if (submission.inFlight && submission.inFlightCorrection) {
        const filing = dwtModeName(submission.filingMode ?? submission.statusMode);
        return submission.inFlightStale
            ? {
                  title: "Correction outcome requires reconciliation",
                  variant: "danger",
                  explanation: `A correction of the ${filing} filing started but its outcome was never recorded. The filing still exists; refresh its status with ${submission.provider} before doing anything else.`,
              }
            : { title: "Correcting the DEFRA filing…", variant: "info", explanation: `A correction of the ${filing} filing is being sent. The filing stands until DEFRA accepts it.` };
    }
    if (submission.inFlight) {
        return submission.inFlightStale
            ? {
                  title: "Submission outcome requires reconciliation",
                  variant: "danger",
                  explanation: `A ${mode} submission started but its outcome was never recorded. It may already have been filed, so it must not be sent again until its status has been checked with ${submission.provider}.`,
              }
            : { title: "Submitting…", variant: "info", explanation: `A ${mode} submission is in progress.` };
    }
    if (submission.status === "Uncertain" && submission.lastCorrection?.status === "Uncertain" && submission.lastCorrection.mode === submission.statusMode) {
        return {
            title: "Correction outcome requires reconciliation",
            variant: "danger",
            explanation: `Froom could not confirm whether the last correction of this ${mode} filing was applied. The filing still exists and will not be filed again; refresh its status before correcting it again.`,
        };
    }
    switch (submission.status) {
        case "CorrectionPending":
            return {
                title: "Filed with DEFRA - correction pending",
                variant: "info",
                explanation: `A correction of this ${mode} filing is with ${submission.provider}; DEFRA still holds the previous details and ${submission.provider} retries automatically. It will not be filed again; refresh its status to see when the correction is applied.`,
            };
        case "CorrectionRejected":
            return {
                title: "Filed with DEFRA - correction rejected",
                variant: "warning",
                explanation: "DEFRA refused the last correction; the filing stands, unchanged. Fix the movement, save it and correct the filing again.",
            };
        case "CorrectionFailed":
            return {
                title: "Filed with DEFRA - correction failed",
                variant: "warning",
                explanation: `The last correction could not be delivered; the filing stands. ${dwtFailureReasonText(submission.providerReason)} The correction may be sent again.`,
            };
        case "ProviderMismatch":
            return {
                title: "DEFRA filing requires provider investigation",
                variant: "danger",
                explanation: `${submission.provider}'s record of this ${mode} filing and DEFRA's (or Froom's) disagree${
                    submission.providerReason || submission.providerErrorCode ? ` (${submission.providerReason ?? submission.providerErrorCode})` : ""
                }. It is not known which details DEFRA holds. Nothing can be submitted or corrected until ${submission.provider} has investigated; refresh its status to check.`,
            };
        case "Validated":
            return {
                title: "Dry validation successful",
                variant: "success",
                explanation: "AxiWaste checked the movement and built the DEFRA receipt. Nothing was sent to DEFRA and there is no waste tracking ID.",
            };
        case "Accepted":
            return {
                title: submission.statusMode === "live" ? "Submitted - accepted by DEFRA" : `Submitted - accepted by ${mode}`,
                variant: "success",
                explanation:
                    submission.statusMode === "live"
                        ? "DEFRA holds this receipt."
                        : "Accepted by DEFRA's test service. This is not a real filing.",
            };
        case "Queued":
            return {
                title: "Processing",
                variant: "info",
                explanation: `${submission.provider} has the submission and is retrying it with DEFRA automatically. Do not resubmit.`,
            };
        case "OutcomeUnknown":
            return {
                title: "Submission outcome requires reconciliation",
                variant: "danger",
                explanation: `The outcome is unknown and the movement may already have been filed. ${submission.provider} checks with DEFRA. Do not resubmit.`,
            };
        case "Uncertain":
            return {
                title: "Submission outcome requires reconciliation",
                variant: "danger",
                explanation: "Froom could not confirm what happened to the last submission, so it may already have been filed. It will not be sent again until its status has been checked.",
            };
        case "Invalid":
            return {
                title: `Not accepted - ${submission.provider}'s checks refused it`,
                variant: "warning",
                explanation: "Nothing was filed. Correct the movement, save it, and submit again. The same DWT reference is used.",
            };
        case "Rejected":
            return {
                title: "Rejected by DEFRA",
                variant: "warning",
                explanation: "Nothing was filed. Correct the movement, save it, and submit again. The same DWT reference is used.",
            };
        case "Failed":
            return {
                title: "Submission failed",
                variant: "danger",
                explanation: `${dwtFailureReasonText(submission.providerReason)} It is not resubmitted until its status has been re-checked.`,
            };
        case "Cancelled":
            return { title: "Cancelled by AxiWaste", variant: "warning", explanation: "Nothing was filed. It may be submitted again." };
        case "AlreadyFiled":
            return dwtCorrectionStateView(submission) ?? {
                title: "Already filed with DEFRA",
                variant: "danger",
                explanation: `${submission.provider} reports this reference is already filed. It will not be filed again; its status must be reconciled and any change needs a correction.`,
            };
        case "Refused":
            return { title: "Refused by AxiWaste", variant: "warning", explanation: `Nothing was filed. ${dwtRefusalText(submission.providerErrorCode)}` };
        case "NotSent":
            return { title: "Not sent", variant: "warning", explanation: "AxiWaste could not be reached, so nothing was sent. Try again." };
        case "ProviderSubmitting":
            return {
                title: `Being submitted by ${submission.provider}`,
                variant: "info",
                explanation: `${submission.provider} reported that it is submitting this movement to ${mode} now. Do not resubmit; refresh its status shortly.`,
            };
        case "NoProviderRecord":
            return {
                title: `Not held by ${submission.provider}`,
                variant: "warning",
                explanation: `${submission.provider} confirmed it holds no ${mode} record of this reference, so nothing was filed there. It may be submitted again; the same DWT reference is used.`,
            };
        default:
            return { title: "Not submitted", variant: "secondary", explanation: "" };
    }
}

/**
 * A filing recorded before S2D whose correction state AxiWaste reported through a status check (newer checks report the
 * correction statuses themselves).
 */
function dwtCorrectionStateView(submission: IWasteMovementDwtSubmission): DwtStatusView | null {
    const noCorrections = "It will not be filed again; refresh its status to settle it.";
    switch (submission.providerStatus) {
        case "amending":
            return {
                title: "Filed with DEFRA - correction in progress",
                variant: "info",
                explanation: `A correction is being sent by ${submission.provider}; the original details still stand at DEFRA. ${noCorrections}`,
            };
        case "amend_queued":
            return {
                title: "Filed with DEFRA - correction queued",
                variant: "info",
                explanation: `DEFRA could not be reached for a correction, so the previous details still stand; ${submission.provider} retries the correction automatically. ${noCorrections}`,
            };
        case "amend_rejected":
            return {
                title: "Filed with DEFRA - correction rejected",
                variant: "warning",
                explanation: `DEFRA refused a correction; the original filing stands, unchanged. ${noCorrections}`,
            };
        case "amend_failed":
            return {
                title: "Filed with DEFRA - correction failed",
                variant: "warning",
                explanation: `A correction could not be delivered; the original filing stands. ${noCorrections}`,
            };
        default:
            return null;
    }
}

export function dwtDeadlineText(submission: IWasteMovementDwtSubmission): { text: string; variant: DwtVariant } | null {
    const deadline = submission.deadline;
    if (!deadline || (!deadline.dueAtUtc && !deadline.dueDate)) return null;
    const when = deadline.dueAtUtc ? formatUkInstant(deadline.dueAtUtc) : deadline.dueDate ?? "";
    const state = deadline.state === "overdue" ? "overdue" : deadline.state === "at-risk" ? "due within 24 hours" : "on time";
    const approximate = deadline.approximate ? " (approximate - the real deadline may be a little later)" : "";
    return {
        text: `Must be recorded with DEFRA by ${when}: ${state}${approximate}.`,
        variant: deadline.state === "overdue" ? "danger" : deadline.state === "at-risk" ? "warning" : "secondary",
    };
}

/** S2C: the refresh button's words - naming the mode when the record it checks is not the one the status shows. */
export function dwtRefreshButtonLabel(submission: IWasteMovementDwtSubmission): string {
    return submission.refreshMode && submission.refreshMode !== submission.statusMode
        ? `Refresh DWT status (${dwtModeName(submission.refreshMode)})`
        : "Refresh DWT status";
}

/** S2C: when the status was last checked with the provider, and what that check did. */
export function dwtLastCheckText(submission: IWasteMovementDwtSubmission): { text: string; settled: boolean } | null {
    if (!submission.lastCheckedAtUtc) return null;
    const when = `Last checked with ${submission.provider}: ${formatUkInstant(submission.lastCheckedAtUtc)} (${dwtModeName(submission.lastCheckMode)})`;
    return submission.lastCheckStatus === "Inconclusive"
        ? { text: `${when} - not confirmed${submission.lastCheckProblem ? `: ${submission.lastCheckProblem}` : "."}`, settled: false }
        : { text: `${when} - confirmed.`, settled: true };
}

/**
 * S2C: what to tell the operator after a refresh request. A 200 means the provider was asked: either its answer was applied
 * (the status block shows it) or it settled nothing and the message says why. Anything else explains why nothing was asked.
 */
export function dwtRefreshFeedback(httpStatus: number, body: IWasteMovementDwtRefreshResponse): DwtSubmitFeedback {
    const message = body.message ?? null;
    switch (httpStatus) {
        case 200:
            return body.reconciled
                ? { variant: "success", message: message ?? "The status was confirmed." }
                : { variant: "warning", message: message ?? "The status could not be confirmed, so nothing has changed. Try again later." };
        case 409:
            return { variant: "info", message: message ?? "There is nothing to check at the moment." };
        case 503:
            return { variant: "danger", message: message ?? "DWT status checks are not configured on this server." };
        default:
            return { variant: "danger", message: message ?? "The status could not be checked. Nothing has changed; try again." };
    }
}

/**
 * What to tell the operator after a submit request, from its HTTP status and Froom's answer. A 200 means an attempt was
 * made and the status block shows its outcome; everything else explains why no attempt was made, or - for an outcome that
 * could not be saved - what AxiWaste said.
 */
export function dwtSubmitFeedback(httpStatus: number, body: IWasteMovementDwtSubmitResponse): DwtSubmitFeedback | null {
    const message = body.message ?? null;
    switch (httpStatus) {
        case 200:
            return body.replay
                ? { variant: "info", message: `${body.submission?.provider ?? "The provider"} already held this answer, so nothing was sent to DEFRA again.` }
                : null;
        case 409:
            return { variant: "warning", message: message ?? "A submission is not permitted at the moment." };
        case 422:
            return { variant: "warning", message: message ?? "This movement is not ready for DEFRA submission." };
        case 503:
            return { variant: "danger", message: message ?? "DEFRA submission is not configured on this server." };
        case 500:
            if (body.code === "DWT_OUTCOME_NOT_SAVED") {
                const said = body.outcomeStatus
                    ? ` The answer was: ${body.outcomeStatus}${body.outcomeWasteTrackingId ? `, waste tracking ID ${body.outcomeWasteTrackingId}` : ""}.`
                    : "";
                return {
                    variant: "danger",
                    message: `${message ?? "The submission was sent but its outcome could not be saved."}${said} Note this and contact support.`,
                };
            }
            return {
                variant: "danger",
                message: message ?? "The server could not complete the submission. It may have been sent - check the status below before trying again.",
            };
        default:
            return { variant: "danger", message: message ?? "The submission request was not accepted." };
    }
}

/** S2D: the filing a correction changes, as the operator must read it: "DEFRA Test filing" / "DEFRA Live filing". */
export function dwtFilingName(mode: string | null | undefined): string {
    return `${dwtModeName(mode)} filing`;
}

/** S2D: the correction dialog's title; LIVE says plainly that it changes the real filing. */
export function dwtCorrectionConfirmTitle(mode: string | null | undefined): string {
    return mode === "live" ? "Correct the LIVE DEFRA filing" : `Correct the ${dwtFilingName(mode)}`;
}

/** S2D: the correction dialog's confirm button. */
export function dwtCorrectionConfirmButtonLabel(mode: string | null | undefined): string {
    return mode === "live" ? "Yes, correct the LIVE DEFRA filing" : `Yes, correct the ${dwtFilingName(mode)}`;
}

/** S2D: characters as the contract counts them (Unicode code points), after surrounding spaces are removed. */
export function dwtReasonLength(reason: string): number {
    return Array.from(reason.trim()).length;
}

/** S2D: why this reason cannot be sent (the server checks it again), or null. */
export function dwtReasonProblem(reason: string, min = 10, max = 500): string | null {
    const length = dwtReasonLength(reason);
    if (length < min) return `Give a reason of at least ${min} characters. It is kept with the DEFRA filing and shown to the regulator.`;
    if (length > max) return `The reason must be at most ${max} characters (it is ${length}).`;
    return null;
}

/** S2D: what an AxiWaste refusal of a correction means for the operator (nothing was sent for any of them). */
export function dwtCorrectionRefusalText(code: string | null | undefined): string {
    switch (code) {
        case "REASON_REQUIRED":
            return "AxiWaste needs a reason of at least 10 characters.";
        case "AMENDED_BY_REQUIRED":
            return "AxiWaste needs to know who made the correction. Contact support.";
        case "APPROVER_REQUIRED":
            return "AxiWaste says this correction changes the weight, EWC or disposal/recovery code, so a second person must approve it. Choose an approver and send it again.";
        case "APPROVER_SAME_PERSON":
            return "The approver must be a different person from the one making the correction.";
        case "INVALID_AMENDMENT":
            return "AxiWaste's checks refused the correction.";
        case "MOVEMENT_TOO_LARGE":
            return "The movement is too large for AxiWaste. Shorten the longest text and try again.";
        default:
            return dwtRefusalText(code);
    }
}

/** S2D: what happened to the latest correction, in one line. */
export function dwtLastCorrectionText(correction: IDwtCorrectionAttempt): string {
    const when = formatUkInstant(correction.completedAtUtc ?? correction.startedAtUtc);
    const by = correction.amendedBy ? ` by ${correction.amendedBy}` : "";
    const approved = correction.approvedBy ? `, approved by ${correction.approvedBy}` : "";
    const outcome = (() => {
        switch (correction.status) {
            case "Accepted":
                return correction.replay ? "DEFRA already held these details" : "accepted by DEFRA";
            case "CorrectionPending":
                return "pending with the provider";
            case "CorrectionRejected":
                return "rejected by DEFRA - the filing stands";
            case "CorrectionFailed":
                return "not delivered - the filing stands";
            case "CorrectionRefused":
                return `refused before sending (${correction.providerErrorCode ?? `HTTP ${correction.httpStatusCode ?? "?"}`}) - nothing changed`;
            case "ProviderMismatch":
                return "requires provider investigation";
            case "NotSent":
                return "not sent - nothing changed";
            case "Submitting":
                return "being sent";
            default:
                return "outcome not confirmed";
        }
    })();
    return `Last correction (${dwtModeName(correction.mode)}): ${when}${by}${approved} - ${outcome}.`;
}

/**
 * S2D: what to tell the operator after a correction request. A 200 means the correction was sent: the outcome says what
 * happened to it (DEFRA may still have refused it). Anything else explains why nothing was sent.
 */
export function dwtCorrectFeedback(httpStatus: number, body: IWasteMovementDwtCorrectResponse): DwtSubmitFeedback {
    const message = body.message ?? null;
    const wtid = body.submission?.filingWasteTrackingId ?? body.submission?.wasteTrackingId;
    const provider = body.submission?.provider ?? "AxiWaste";
    if (httpStatus === 200) {
        switch (body.outcomeStatus) {
            case "Accepted":
                return body.replay
                    ? { variant: "info", message: `DEFRA already held these details, so nothing was sent again. The filing is unchanged${wtid ? ` (waste tracking ID ${wtid})` : ""}.` }
                    : { variant: "success", message: `DEFRA accepted the correction. The waste tracking ID is unchanged${wtid ? `: ${wtid}` : ""}.` };
            case "CorrectionPending":
                return { variant: "info", message: `${provider} has the correction and retries it with DEFRA automatically; DEFRA still holds the previous details. Refresh the status later.` };
            case "CorrectionRejected":
                return { variant: "warning", message: "DEFRA refused the correction. The filing stands unchanged; see DEFRA's errors, fix the movement and correct it again." };
            case "CorrectionFailed":
                return { variant: "warning", message: `The correction could not be delivered; the filing stands. ${dwtFailureReasonText(body.submission?.providerReason)}` };
            case "CorrectionRefused":
                return { variant: "warning", message: `${provider} refused the correction before sending anything, so the filing is unchanged. ${dwtCorrectionRefusalText(body.submission?.lastCorrection?.providerErrorCode)}` };
            case "ProviderMismatch":
                return { variant: "danger", message: `${provider}'s record and DEFRA's disagree about this filing. It requires investigation by ${provider}; nothing can be submitted or corrected until it is settled.` };
            case "NotSent":
                return { variant: "warning", message: `${provider} could not be reached, so nothing was sent. The filing is unchanged; try again.` };
            default:
                return { variant: "danger", message: "Froom could not confirm whether the correction was applied. The filing still exists; refresh its status before doing anything else." };
        }
    }
    switch (httpStatus) {
        case 400:
        case 409:
        case 422:
            return { variant: "warning", message: message ?? "The correction was not sent." };
        case 503:
            return { variant: "danger", message: message ?? "DEFRA submission is not configured on this server." };
        case 500:
            return body.code === "DWT_OUTCOME_NOT_SAVED"
                ? { variant: "danger", message: `${message ?? "The correction was sent but its outcome could not be saved."} Note this and contact support; refresh the status before correcting again.` }
                : { variant: "danger", message: message ?? "The correction could not be completed. Refresh the status before trying again." };
        default:
            return { variant: "danger", message: message ?? "The correction request was not accepted." };
    }
}
